import { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Space, message, InputNumber, Descriptions, Tag, Table, Collapse } from 'antd';
import { SwapOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { getChannelLogo, getCurrencyIcon, buildChannelOptions } from '../utils/channelLogos';
import { testApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { getEnvByHostname } from '../utils/env';
import dayjs from 'dayjs';

const sectionStyle = { fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #f0f0f0' };
const sectionStyleMt = { ...sectionStyle, marginTop: 8 };
const gridCol2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };
const gridCol3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 };

const PAYEE_TYPES = [
  { label: 'BENEFICIARY', value: 'BENEFICIARY' },
  { label: 'SUPPLIER', value: 'SUPPLIER' },
];

const COUNTRY_OPTIONS = [
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴' },
  { code: 'BG', name: 'Bulgaria', flag: '🇧🇬' },
  { code: 'HR', name: 'Croatia', flag: '🇭🇷' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦' },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼' },
  { code: 'BH', name: 'Bahrain', flag: '🇧🇭' },
  { code: 'OM', name: 'Oman', flag: '🇴🇲' },
  { code: 'JO', name: 'Jordan', flag: '🇯🇴' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
  { code: 'TZ', name: 'Tanzania', flag: '🇹🇿' },
  { code: 'UG', name: 'Uganda', flag: '🇺🇬' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'PE', name: 'Peru', flag: '🇵🇪' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: 'NP', name: 'Nepal', flag: '🇳🇵' },
  { code: 'MM', name: 'Myanmar', flag: '🇲🇲' },
  { code: 'KH', name: 'Cambodia', flag: '🇰🇭' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },
].sort((a, b) => a.name.localeCompare(b.name));

export default function Quote() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [rateData, setRateData] = useState(null);
  const [channelCodes, setChannelCodes] = useState([]);
  const [channelCurrencyMap, setChannelCurrencyMap] = useState({});
  const [selectedChannel, setSelectedChannel] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await testApi.getChannelCurrencies();
        const raw = res?.data?.data?.responseBody || res?.data?.data || {};
        const currMap = {};
        if (typeof raw === 'object') {
          Object.entries(raw).forEach(([ch, currencies]) => {
            if (Array.isArray(currencies)) currMap[ch] = currencies.sort();
          });
        }
        setChannelCurrencyMap(currMap);
        setChannelCodes(Object.keys(currMap).filter(c => ['PYVIO', 'TAZAPAY', 'MDAQ'].includes(c)).sort());
      } catch { /* ignore */ }
    })();
  }, []);

  const GLOBAL_CURRENCIES = [
    'USD','EUR','GBP','SGD','HKD','JPY','AUD','CAD','CHF','CNY',
    'INR','MYR','THB','PHP','IDR','VND','KRW','TWD','NZD','SEK',
    'NOK','DKK','PLN','CZK','HUF','RON','BGN','TRY','ZAR','BRL',
    'MXN','COP','CLP','PEN','ARS','AED','SAR','QAR','KWD','BHD',
    'OMR','JOD','EGP','NGN','KES','GHS','TZS','UGX',
  ];
  const currencyOptions = () => {
    return GLOBAL_CURRENCIES.map(c => ({ label: `${getCurrencyIcon(c)} ${c}`, value: c }));
  };

  const handleQuery = async (values) => {
    setLoading(true);
    setRateData(null);
    try {
      const requestBody = {
        requestId: `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        channelCode: values.channelCode,
        originCurrency: values.originCurrency,
        targetCurrency: values.targetCurrency,
        payeeType: values.payoutType || values.payeeType,
        payoutType: undefined,
        userId: values.userId || undefined,
        externalUserId: values.externalUserId || undefined,
        destinationCountry: values.destinationCountry || undefined,
        amount: values.amount || undefined,
        localPaymentNetworkType: values.localPaymentNetworkType || undefined,
        fundTransferNetwork: values.fundTransferNetwork || undefined,
        beneficiaryId: values.beneficiaryId || undefined,
        purposeOfTransfer: values.purposeOfTransfer || undefined,
      };
      const username = localStorage.getItem('username') || 'admin';
      const { data } = await testApi.executeTest({
        testType: 'RATE_QUERY',
        channelCode: values.channelCode,
        businessPartner: values.businessPartner || 'test',
        currency: values.originCurrency,
        env: getEnvByHostname(),
        executor: username,
        requestBody: JSON.stringify(requestBody),
      });
      const res = data;
      if (res.data?.responseBody && typeof res.data.responseBody === 'string') {
        try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch {}
      }
      setResult(res);
      // Extract rate data from response
      const respBody = res.data?.responseBody;
      if (respBody?.data) setRateData(respBody.data);
      if (res.data?.success) message.success(t('querySuccess'));
      else message.warning(t('queryFailed') + ': ' + (res.data?.errorMessage || res.msg || ''));
    } catch (error) {
      message.error(t('queryFailed') + ': ' + (error.response?.data?.msg || error.message));
      setResult(error.response?.data || { error: error.message });
    } finally { setLoading(false); }
  };

  const handleReset = () => { form.resetFields(); setResult(null); setRateData(null); setSelectedChannel(null); };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1600, margin: '0 auto' }}>
      <div className="fade-in-up">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space size={12}>
            <SwapOutlined style={{ color: '#667eea', fontSize: 20 }} />
            <span style={{ fontSize: 20, fontWeight: 600 }}>{t('quoteTitle')}</span>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>{t('reset')}</Button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Left: Form */}
          <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 24 }}>
            <Form form={form} layout="vertical" onFinish={handleQuery} initialValues={{ payeeType: 'BENEFICIARY', businessPartner: 'test', amount: 10000 }}>
              <Form.Item name="businessPartner" hidden><Input /></Form.Item>

              <div style={sectionStyle}>📋 {t('quoteBasicInfo')}</div>
              <div style={gridCol3}>
                <Form.Item label={t('channelCode')} name="channelCode" rules={[{ required: true, message: t('pleaseSelect', t('channelCode')) }]}>
                  <Select placeholder={t('selectChannel')} showSearch optionFilterProp="label"
                    options={buildChannelOptions(channelCodes)}
                    onChange={v => { setSelectedChannel(v); form.setFieldsValue({ originCurrency: undefined, targetCurrency: undefined }); }} />
                </Form.Item>
                <Form.Item label={t('quoteOriginCurrency')} name="originCurrency" rules={[{ required: true }]}>
                  <Select placeholder={t('selectCurrency')} showSearch options={currencyOptions()}
                    filterOption={(input, opt) => (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item label={t('quoteTargetCurrency')} name="targetCurrency" rules={[{ required: true }]}>
                  <Select placeholder={t('selectCurrency')} showSearch options={currencyOptions()}
                    filterOption={(input, opt) => (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
              </div>

              <div style={gridCol2}>
                <Form.Item label="支付方式 (payoutType)" name="payoutType" rules={[{ required: true, message: '请选择支付方式' }]}>
                  <Select placeholder="选择支付方式" options={
                    selectedChannel === 'MDAQ' ? [
                      { label: '🏠 LOCAL', value: 'LOCAL' },
                      { label: '🌍 SWIFT-SHA', value: 'SWIFT-SHA' },
                      { label: '🌍 SWIFT-OUR', value: 'SWIFT-OUR' },
                      { label: '🔄 INTERNAL', value: 'INTERNAL' },
                    ] : [
                      { label: '🌍 SWIFT', value: 'swift' },
                      { label: '🏠 Local', value: 'local' },
                      { label: '🏦 Local Payment Network', value: 'local_payment_network' },
                    ]
                  } />
                </Form.Item>
                <Form.Item label={selectedChannel === 'MDAQ' ? '金额 (元)' : t('quoteAmount')} name="amount" tooltip={selectedChannel === 'MDAQ' ? '单位为元，如 100 表示 100.00' : 'minor units, 50000=500.00, 默认10000'} rules={[{ required: true, message: '请输入金额' }]}>
                  <InputNumber style={{ width: '100%' }} min={1} placeholder={selectedChannel === 'MDAQ' ? '100' : '50000'} defaultValue={10000} />
                </Form.Item>
              </div>

              <div style={sectionStyleMt}>👤 {t('quoteUserInfo')}</div>
              <div style={gridCol2}>
                <Form.Item label={t('quoteUserId')} name="userId" tooltip={t('quoteUserIdTip')}>
                  <Input placeholder="UR20260418..." style={{ fontFamily: 'monospace' }} />
                </Form.Item>
                <Form.Item label={t('quoteExternalUserId')} name="externalUserId" rules={[{ required: selectedChannel === 'MDAQ', message: 'MDAQ渠道必填' }]}>
                  <Input placeholder="ext-user-001" />
                </Form.Item>
              </div>

              <div style={sectionStyleMt}>🔧 {t('quoteAdvanced')}</div>
              {selectedChannel !== 'MDAQ' && (
              <div style={gridCol3}>
                <Form.Item label={t('quoteDestCountry')} name="destinationCountry" tooltip="ISO 2-letter code, 非必填">
                  <Select placeholder={t('quoteDestCountry')} showSearch optionFilterProp="label" allowClear
                    options={COUNTRY_OPTIONS.map(c => ({ label: `${c.flag} ${c.name} (${c.code})`, value: c.code }))}
                    filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
                </Form.Item>
                <Form.Item label={t('quoteLocalNetwork')} name="localPaymentNetworkType" tooltip="e.g. pix_brl">
                  <Input placeholder="pix_brl" />
                </Form.Item>
                <Form.Item label={t('quoteFundNetwork')} name="fundTransferNetwork" tooltip="e.g. sepa, ach, pix">
                  <Input placeholder="sepa" />
                </Form.Item>
              </div>
              )}
              {selectedChannel === 'MDAQ' && (
                <div style={gridCol2}>
                  <Form.Item label="Beneficiary ID" name="beneficiaryId" rules={[{ required: true, message: 'MDAQ必填' }]} tooltip="绑定银行查询返回字段：channelRecipientId">
                    <Input placeholder="16e260bd-07cf-4093-966f-6ea193abc067" style={{ fontFamily: 'monospace' }} />
                  </Form.Item>
                  <Form.Item label="Purpose of Transfer" name="purposeOfTransfer" rules={[{ required: true, message: 'MDAQ必填' }]} tooltip="转账目的代码，如 INTC">
                    <Input placeholder="INTC" />
                  </Form.Item>
                </div>
              )}

              <Button type="primary" htmlType="submit" icon={<SwapOutlined />} loading={loading} size="large" block style={{ height: 44, marginTop: 12 }}>
                {loading ? t('loading') : t('quoteQueryBtn')}
              </Button>
            </Form>
          </div>

          {/* Right: Result */}
          <div>
            <div style={sectionStyle}>📋 {t('quoteResultTitle')}</div>

            {/* Rate card */}
            {rateData && (
              <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 12, padding: 24, marginBottom: 16, color: '#fff' }}>
                <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 12 }}>{t('quoteExchangeRate')}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                  <span style={{ fontSize: 14 }}>{getCurrencyIcon(rateData.originCurrency)} {rateData.originCurrency}</span>
                  <SwapOutlined style={{ fontSize: 20 }} />
                  <span style={{ fontSize: 14 }}>{getCurrencyIcon(rateData.targetCurrency)} {rateData.targetCurrency}</span>
                  <span style={{ fontSize: 32, fontWeight: 700, marginLeft: 'auto' }}>{rateData.exchangeRate || '—'}</span>
                </div>
              </div>
            )}

            {/* Rate details table */}
            {rateData && (
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', padding: 20, marginBottom: 16 }}>
                <Table
                  dataSource={[
                    { key: 'rateId', field: 'Rate ID', value: rateData.rateId },
                    { key: 'pair', field: '币种对 / Currency Pair', value: rateData.originCurrency && rateData.targetCurrency ? `${rateData.originCurrency} → ${rateData.targetCurrency}` : undefined },
                    { key: 'rate', field: t('quoteExchangeRate'), value: rateData.exchangeRate },
                    { key: 'originAmount', field: '原始金额 / Origin Amount', value: rateData.originAmount },
                    { key: 'targetAmount', field: '目标金额 / Target Amount', value: rateData.targetAmount },
                    { key: 'fee', field: '手续费 / Fee', value: rateData.fee ?? rateData.feeAmount },
                    { key: 'payoutType', field: '支付方式 / Payout Type', value: rateData.payoutType },
                    { key: 'expireTime', field: t('quoteExpireTime'), value: (() => {
                      if (!rateData.expireTime) return undefined;
                      const raw = Number(rateData.expireTime);
                      if (isNaN(raw)) return String(rateData.expireTime);
                      // Try as-is first (milliseconds), then as seconds
                      const asMs = dayjs(raw);
                      if (asMs.isValid() && asMs.year() >= 2000 && asMs.year() <= 2100) return asMs.format('YYYY-MM-DD HH:mm:ss');
                      const asSec = dayjs(raw * 1000);
                      if (asSec.isValid() && asSec.year() >= 2000 && asSec.year() <= 2100) return asSec.format('YYYY-MM-DD HH:mm:ss');
                      return dayjs(raw).format('YYYY-MM-DD HH:mm:ss');
                    })() },
                  ].filter(r => r.value !== undefined && r.value !== null && r.value !== '')}
                  columns={[
                    { title: '字段 / Field', dataIndex: 'field', key: 'field', width: 200,
                      render: v => <span style={{ color: '#64748b', fontSize: 13 }}>{v}</span> },
                    { title: '值 / Value', dataIndex: 'value', key: 'value',
                      render: (v, record) => {
                        if (record.key === 'rateId') return <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#334155', wordBreak: 'break-all' }}>{v}</span>;
                        if (record.key === 'rate') return <Tag color="blue" style={{ fontSize: 14, padding: '2px 12px' }}>{v}</Tag>;
                        if (record.key === 'expireTime') return <Tag color="orange">{v}</Tag>;
                        if (record.key === 'pair') return <Tag color="purple">{v}</Tag>;
                        if (record.key === 'fee') return <span style={{ color: '#ef4444', fontWeight: 500 }}>{v}</span>;
                        return <span style={{ fontWeight: 500, color: '#1e293b' }}>{v}</span>;
                      }
                    },
                  ]}
                  pagination={false}
                  size="middle"
                  showHeader
                />
              </div>
            )}

            {/* Raw JSON (collapsible) */}
            <Collapse
              size="small"
              style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0' }}
              items={result ? [{
                key: 'raw',
                label: <span style={{ fontSize: 13, color: '#64748b' }}>🔍 原始响应 / Raw Response</span>,
                children: (
                  <ReactJson src={result} collapsed={1} theme="rjv-default" displayDataTypes={false} displayObjectSize={false} enableClipboard />
                ),
              }] : [{
                key: 'empty',
                label: <span style={{ fontSize: 13, color: '#9ca3af' }}>🔍 原始响应 / Raw Response</span>,
                children: (
                  <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8, border: '2px dashed #e5e7eb' }}>
                    <SwapOutlined style={{ fontSize: 40, color: '#d1d5db' }} />
                    <div style={{ marginTop: 12 }}>{t('quoteEmptyHint')}</div>
                  </div>
                ),
              }]}
            />

            {/* Status indicator */}
            {result && (
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b' }}>
                {result.data?.success
                  ? <><CheckCircleOutlined style={{ color: '#22c55e' }} /> 查询成功</>
                  : <><CloseCircleOutlined style={{ color: '#ef4444' }} /> {result.data?.errorMessage || result.msg || '查询失败'}</>
                }
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
